import React, { useState, useEffect } from "react";
// import { HiChevronUp, HiChevronDown } from "react-icons/hi2";
import FastAPIClient from "../../../src/client";
import config from "../../../src/config";
import TimeEntry from "./TimeEntry"; // Import the TimeEntry component
import { Button } from "@mui/material";
import Alert from "@mui/material/Alert";
import { CircularProgress, Skeleton } from "@mui/material";

// Initialize FastAPI client
const client = new FastAPIClient(config);

interface DailyViewProps {
    date: string; // Date passed from the parent component
    user_id: string; // User ID for fetching the correct kilometers
}

const DelayedSkeleton = () => {
    const [opacity, setOpacity] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setOpacity(1); // Increase opacity after 1 second
        }, 250);

        return () => clearTimeout(timer); // Cleanup timeout
    }, []);

    return (
        <div className="space-y-4 p-4">
            <div className="">
                <Skeleton
                    variant="rounded"
                    width="100%"
                    height={30}
                    animation="wave"
                    sx={{ opacity, transition: "opacity 1.5s ease-in-out" }}
                />
            </div>
        </div>
    );
};

const DailyView: React.FC<DailyViewProps> = ({ date, user_id }) => {
    const [isExpanded, setIsExpanded] = useState(false); // Track if the section is expanded
    const [morningKilometers, setMorningKilometers] = useState<number | null>(
        null
    ); // Morning kilometers state
    const [eveningKilometers, setEveningKilometers] = useState<number | null>(
        null
    ); // Evening kilometers state
    const [isLoading, setIsLoading] = useState(true); // State to manage loading status
    const [isSaving, setIsSaving] = useState(false); // State to manage loading status
    const [newMorningKilometers, setNewMorningKilometers] = useState<
        number | null
    >(null); // Separate state for morning kilometers
    const [newEveningKilometers, setNewEveningKilometers] = useState<
        number | null
    >(null); // Separate state for evening kilometers

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showAlert, setShowAlert] = useState(false);

    const [morningId, setMorningId] = useState<string | null>(null);
    const [eveningId, setEveningId] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null); // Add success message state

    const fetchKilometers = async () => {
        try {
            // Convert date to a 'YYYY-MM-DD' format if it's a timestamp
            setIsLoading(true);
            let formattedDate = date;
            if (date) {
                const parsedDate = new Date(date);
                if (!isNaN(parsedDate.getTime())) {
                    formattedDate = parsedDate.toISOString().split("T")[0]; // Get just the date part
                }
            }

            const response = await client.fetchKilometers(
                user_id,
                formattedDate
            );

            if (response.length > 0) {
                setMorningKilometers(response[0].kilometers);
                setMorningId(response[0].id); // 👈 Save morning log ID
            } else {
                setMorningKilometers(undefined);
                setMorningId(null);
            }
            if (response.length > 1) {
                setEveningKilometers(response[1].kilometers);
                setEveningId(response[1].id); // 👈 Save evening log ID
            } else {
                setEveningKilometers(undefined);
                setEveningId(null);
            }
        } catch (error) {
            console.error("Error fetching kilometers:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch kilometers from API when component mounts or date/user_id change
    useEffect(() => {
        fetchKilometers();
    }, [date]);

    // Function to check if the date is before the current date
    const isTwoDaysAgoOrEarlier = () => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); // Set current date to midnight

        const twoDaysAgo = new Date(currentDate);
        twoDaysAgo.setDate(currentDate.getDate() - 2); // Subtract 2 days

        const logDate = new Date(date);
        logDate.setHours(0, 0, 0, 0); // Set log date to midnight

        return logDate <= twoDaysAgo; // Return true if the date is two days ago or earlier
    };

    // Handle saving new kilometers or updating existing ones
    const handleSaveKilometers = async () => {
        try {
            setIsSaving(true);
            setErrorMessage(null); // Clear previous errors
            setSuccessMessage(null); // Clear previous success messages
            setShowAlert(false);

            // Validation: Evening kilometers must be >= Morning kilometers
            if (
                (newEveningKilometers < newMorningKilometers ||
                    newEveningKilometers < morningKilometers) &&
                newEveningKilometers !== null
            ) {
                setErrorMessage(
                    "Les kilomètres du soir doivent être supérieurs ou égaux aux kilomètres du matin."
                );
                setShowAlert(true);
                setIsSaving(false);
                return;
            }

            if (newMorningKilometers !== null) {
                const parsedDate = new Date(date);
                parsedDate.setHours(8, 0, 0, 0);
                const payload = {
                    user_id,
                    kilometers: newMorningKilometers,
                    tracked_at: parsedDate.toISOString(),
                    id: morningId,
                };

                if (morningId) {
                    await client.updateKilometer(payload); // Update if exists
                } else {
                    await client.createKilometer(payload); // Otherwise, create new
                }
            }

            if (newEveningKilometers !== null) {
                const parsedDate = new Date(date);
                parsedDate.setHours(18, 0, 0, 0);
                const payload = {
                    user_id,
                    kilometers: newEveningKilometers,
                    tracked_at: parsedDate.toISOString(),
                    id: eveningId,
                };

                if (eveningId) {
                    await client.updateKilometer(payload); // Update if exists
                } else {
                    await client.createKilometer(payload); // Otherwise, create new
                }
            }

            // Success Message
            setSuccessMessage("Kilomètres enregistrés avec succès.");
            setShowAlert(true);
            setNewMorningKilometers(null);
            setNewEveningKilometers(null);
            fetchKilometers();

            setIsExpanded(false);
        } catch (error) {
            console.error("Error saving kilometers:", error);
            setErrorMessage(
                "Une erreur est survenue lors de l'enregistrement des kilomètres."
            );
            setShowAlert(true);
        } finally {
            setIsSaving(false);
        }
    };

    // Function to toggle the expanded state (for smaller screens)
    const toggleExpand = () => {
        setIsExpanded((prevState) => !prevState);
    };

    useEffect(() => {
        const handleResize = () => {
            // const isMdOrLarger = window.innerWidth >= 768; // Tailwind 'md' breakpoint (768px)

            if (
                morningKilometers !== undefined ||
                eveningKilometers !== undefined
            ) {
                setIsExpanded(false); // Collapse if both values exist
            } else {
                setIsExpanded(true); // Expand if one or both are missing
            }
        };

        handleResize(); // Run once on mount
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, [morningKilometers, eveningKilometers, date]);

    useEffect(() => {
        setNewMorningKilometers(null);
        setNewEveningKilometers(null);
    }, [date]);

    // Check if editing is disabled
    const isEditingDisabled =
        (morningKilometers !== null || eveningKilometers !== null) &&
        isTwoDaysAgoOrEarlier();

    useEffect(() => {
        setIsExpanded(!isTwoDaysAgoOrEarlier());
    }, [date]);

    useEffect(() => {
        setNewEveningKilometers(eveningKilometers ?? null);
        setNewMorningKilometers(morningKilometers ?? null);
    }, [eveningKilometers, morningKilometers]);

    // Add this function near the top of the component, after the other function declarations
    const getButtonText = () => {
        if (isTwoDaysAgoOrEarlier()) {
            return isExpanded ? "Fermer" : "Afficher";
        }

        if (
            morningKilometers &&
            eveningKilometers &&
            morningKilometers > 0 &&
            eveningKilometers > 0
        ) {
            return isExpanded ? "Fermer" : "Afficher";
        }

        return isExpanded ? "Fermer" : "Saisir";
    };

    return (
        <div
            className={`w-full absolute md:static md:flex md:w-full md:flex-col rounded-t-lg border-t border-gray-200 bottom-0 md:bottom-0 left-0 bg-white z-[9997] pt-2 mb-0`}
        >
            {/* Content header with toggle button for smaller screens */}
            {!isLoading ? (
                <div className="flex flex-row items-center py-4 px-4">
                    <div
                        className={`w-full text-left font-bold ${
                            !morningKilometers || !eveningKilometers
                                ? "text-rose-500"
                                : "text-black"
                        }`}
                    >
                        {morningKilometers > 0 && eveningKilometers > 0 ? (
                            <>
                                Kilomètres:{" "}
                                {eveningKilometers - morningKilometers} km
                            </>
                        ) : isTwoDaysAgoOrEarlier() ? (
                            "Vous n'avez renseigné aucun kilométrage."
                        ) : (
                            "Vous n'avez pas encore renseigné vos kilomètres du jour !"
                        )}
                    </div>
                    {/* Show toggle button only on small screens */}
                    <button
                        onClick={toggleExpand}
                        className={`rounded-full px-2 py-1 text-sm font-bold ${
                            morningKilometers &&
                            eveningKilometers &&
                            morningKilometers > 0 &&
                            eveningKilometers > 0
                                ? "bg-gold text-black"
                                : "bg-rose-500 text-white"
                        } ${
                            morningKilometers !== null &&
                            eveningKilometers !== null
                                ? ""
                                : "md:hidden"
                        }`}
                    >
                        {getButtonText()}
                    </button>
                </div>
            ) : (
                <DelayedSkeleton />
            )}

            {/* Kilometers entry section */}
            <div
                className={`mt-2 space-y-2 justify-start w-full text-sm transition-all duration-300 ease-in-out overflow-hidden px-4  ${
                    isExpanded ? "max-h-[200px]" : "max-h-0" // Set max height for expansion and collapse
                } md:max-h-none`} // Ensure the content is always expanded above md breakpoint
            >
                {/* Expandable Content */}
                <div className="flex flex-col pb-2 space-y-2">
                    {/* Use the TimeEntry component for kilometers */}
                    {showAlert && (
                        <>
                            {successMessage && (
                                <div className="hidden md:block fixed top-4 left-1/2 transform -translate-x-1/2 w-[90%] sm:w-[24rem] rounded-xl text-white shadow-lg z-50">
                                    <Alert
                                        severity="success"
                                        onClose={() => setShowAlert(false)}
                                    >
                                        {successMessage}
                                    </Alert>
                                </div>
                            )}
                            {errorMessage && (
                                <div className="hidden md:block fixed top-4 left-1/2 transform -translate-x-1/2 w-[90%] sm:w-[24rem] rounded-xl text-white shadow-lg z-50">
                                    <Alert
                                        severity="error"
                                        onClose={() => setShowAlert(false)}
                                    >
                                        {errorMessage}
                                    </Alert>
                                </div>
                            )}
                            <div className="fixed top-4 left-4 right-4 rounded-xl text-white shadow-lg z-50 md:hidden">
                                {successMessage && (
                                    <Alert
                                        severity="success"
                                        onClose={() => setShowAlert(false)}
                                    >
                                        {successMessage}
                                    </Alert>
                                )}
                                {errorMessage && (
                                    <Alert
                                        severity="error"
                                        onClose={() => setShowAlert(false)}
                                    >
                                        {errorMessage}
                                    </Alert>
                                )}
                            </div>
                        </>
                    )}
                    {isExpanded && !isLoading && (
                        <div className="w-full">
                            <div className="flex flex-row w-full">
                                <div className="flex w-full ">
                                    <TimeEntry
                                        label="Début"
                                        value={newMorningKilometers}
                                        onChange={(value) =>
                                            !isEditingDisabled &&
                                            setNewMorningKilometers(value)
                                        }
                                        disabled={isEditingDisabled}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-row w-full mt-2 mb-2">
                                <div className="flex w-full">
                                    <TimeEntry
                                        label="Fin"
                                        value={newEveningKilometers}
                                        onChange={(value) =>
                                            !isEditingDisabled &&
                                            setNewEveningKilometers(value)
                                        }
                                        disabled={isEditingDisabled}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-row w-full">
                                <div className="w-1/2 hidden md:block"></div>
                                <div className="flex w-full md:w-1/2 md:justify-end">
                                    {!isEditingDisabled && (
                                        <Button
                                            onClick={handleSaveKilometers}
                                            variant="contained"
                                            color="primary"
                                            disabled={
                                                isEditingDisabled || isSaving
                                            } // Disable if loading
                                            sx={{
                                                width: {
                                                    xs: "100%",
                                                    md: "auto",
                                                },
                                                display: "flex", // Ensure the loader and text align correctly
                                                justifyContent: "center", // Center the text and loader
                                                alignItems: "center", // Center the content vertically
                                            }}
                                        >
                                            {isSaving ? (
                                                <CircularProgress
                                                    size={24}
                                                    color="inherit"
                                                />
                                            ) : (
                                                "Enregistrer"
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DailyView;
